import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const TOKEN_KEY = 'mcr_auth_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try {
      const t = sessionStorage.getItem(TOKEN_KEY);
      if (t) return t;
      // Migrate from localStorage if exists
      const legacy = localStorage.getItem('token');
      if (legacy) {
        sessionStorage.setItem(TOKEN_KEY, legacy);
        localStorage.removeItem('token');
        return legacy;
      }
      return null;
    } catch { return null; }
  });

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) { console.error("Logout cleanup:", e); }
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (err) {
      console.error("Auth check failed:", err?.response?.status);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchMe();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password) => {
    // Clear any stale state first
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) { console.error("Session clear:", e); }
    delete axios.defaults.headers.common['Authorization'];

    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    if (!access_token) throw new Error("No token received");
    setToken(access_token);
    setUser(userData);
    try { sessionStorage.setItem(TOKEN_KEY, access_token); } catch (e) { console.error("Token save:", e); }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return userData;
  };

  const value = useMemo(() => ({
    user, loading, login, logout, isAuthenticated: !!user
  }), [user, loading, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
